/** Inviting a colleague, from the Admin's side — **ADR-0046**, and the engagement an invited
 * Auditor must carry (**ADR-0048**): a period, an end date, and **the filings it is about** — which
 * is what turns a window over the data into a window over a company.
 *
 * The routes existed before this file did, and nothing in the product called them: the People
 * screen told its reader that inviting was not built. `POST /invitations` issues a single-use token
 * that is handed over out of band; this is the screen's half.
 *
 * **The token is a bearer credential, so nothing here caches it.** The issuing mutation's *result*
 * is the token, and React Query keeps a mutation's data in its cache for five minutes by default —
 * so `gcTime: 0`, for the reason `claims.ts` gives for the other side of the same exchange. The
 * screen holds it in component state only, and drops it when the Admin says they have passed it on.
 *
 * **The refusals carry codes** (DOC-01) and are exported as a set, so a form puts the sentence
 * beside the field it is about rather than in a banner. The server is the authority on every one of
 * them; the form's own checks only stop it sending a request that is certain to be refused.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { Refused, refusalFrom } from "./refusal";
import type { components } from "./schema";

export type InvitationRole = components["schemas"]["InvitationIn"]["role"];
export type OutstandingInvitation = components["schemas"]["OutstandingInvitationOut"];
export type IssuedInvitation = components["schemas"]["InvitationOut"];

/** What an invitation may carry beyond who and as what. Empty strings are not values: the form
 * passes `undefined` for a field it is not asking, and this turns that into an absent key. */
export interface InvitationRequest {
  email: string;
  role: InvitationRole;
  /** The first and last day of data an Auditor may read. Auditor only — the server refuses them
   * on any other role, so the form does not render them for one. */
  scopeFrom?: string;
  scopeTo?: string;
  /** The last day the person may sign in. Required for an Auditor, optional for every other role. */
  validTo?: string;
  /** The filing obligations an Auditor is engaged for. Required for an Auditor and refused for any
   * other role; the entities they may read are derived from these, so there is no list of entity
   * ids to choose. */
  obligationIds?: string[];
}

/** The three inputs an engagement refusal can be about. */
export type EngagementField = "period" | "end" | "filings";

/** Which input each engagement refusal is about. The codes are the server's (`claims.InvitationRefusal`).
 *
 * `period`, `end` and `filings` rather than a field name, so the form decides what is highlighted.
 * An unrecognised code maps to nothing: a form that guessed a field for a code it has never met
 * would point at the wrong input, which is worse than pointing at none. */
export const ENGAGEMENT_REFUSALS: Readonly<Record<string, EngagementField>> = {
  engagement_period_required: "period",
  engagement_period_incomplete: "period",
  engagement_period_inverted: "period",
  engagement_period_not_applicable: "period",
  engagement_end_required: "end",
  engagement_end_in_the_past: "end",
  engagement_obligations_required: "filings",
  engagement_obligations_not_applicable: "filings",
  engagement_obligation_unknown: "filings",
};

/** `POST /invitations` answers 409 with this code for an address that already belongs to a member
 * of the workspace - active or suspended, told apart by the sentence the server sends. */
export const ALREADY_A_MEMBER = "already_a_member";

/** The field a refusal is about, if it is an engagement refusal. */
export function refusedField(error: unknown): EngagementField | null {
  if (!(error instanceof Refused)) return null;
  return ENGAGEMENT_REFUSALS[error.code] ?? null;
}

export function useInvitations(enabled: boolean) {
  return useQuery({
    queryKey: ["invitations"] as const,
    enabled,
    retry: false,
    queryFn: async (): Promise<OutstandingInvitation[]> => {
      const { data, error, response } = await api.GET("/api/v1/invitations", {});
      // Swallowed, this reads as *"no invitations are outstanding"* — which an Admin acts on by
      // issuing another, and the server answers 409 for the address that already has one.
      if (error) throw refusalFrom(response, error, "The outstanding invitations could not be read.");
      return data ?? [];
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: InvitationRequest): Promise<IssuedInvitation> => {
      const { data, error, response } = await api.POST("/api/v1/invitations", {
        body: {
          email: input.email,
          role: input.role,
          // Spread, not `scope_from: undefined`: the **absence of the key** is what says "no
          // window", and it should not rest on JSON dropping an undefined.
          ...(input.scopeFrom ? { scope_from: input.scopeFrom } : {}),
          ...(input.scopeTo ? { scope_to: input.scopeTo } : {}),
          ...(input.validTo ? { valid_to: input.validTo } : {}),
          // Spread for the same reason: the **absence of the key** is what says "no filing", and
          // an empty array would be a request the server has to interpret rather than read.
          ...(input.obligationIds && input.obligationIds.length > 0
            ? { obligation_ids: input.obligationIds }
            : {}),
        },
      });
      if (error) throw refusalFrom(response, error, "That invitation could not be issued.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

export function useRevokeInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (claimId: string) => {
      const { error, response } = await api.DELETE("/api/v1/invitations/{claim_id}", {
        params: { path: { claim_id: claimId } },
      });
      if (error) throw refusalFrom(response, error, "That invitation could not be withdrawn.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

export type ResentInvitation = components["schemas"]["ResentInvitationOut"];

/** Replace an outstanding invitation with a new one and email it (card C-B, gate 14).
 *
 * **The Admin types the address again**, because the server holds only a digest of it and cannot
 * recover it to send to. The result carries the **new token**, exactly as issuing one does - so it
 * is a bearer credential and gets `gcTime: 0` for `useInviteMember`'s reason - and the Admin can
 * still hand it over themselves, which is what keeps a resend working while SES is sandboxed.
 *
 * The old token stops working in the same transaction, so `["invitations"]` is invalidated: the
 * outstanding list now shows a different row.
 *
 * `invitation_address_mismatch` (422) is exported as a code a form can put beside the address field
 * and is the one refusal here that says so, because the caller is an Admin of the workspace.
 */
export const RESEND_ADDRESS_MISMATCH = "invitation_address_mismatch";

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: { claimId: string; email: string }): Promise<ResentInvitation> => {
      const { data, error, response } = await api.POST("/api/v1/invitations/{claim_id}/resend", {
        params: { path: { claim_id: input.claimId } },
        body: { email: input.email },
      });
      if (error) throw refusalFrom(response, error, "That invitation could not be re-sent.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}
